import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Link,
  Paper,
  InputAdornment,
  FormHelperText,
  Snackbar,
  Alert,
  SlideProps,
  Slide,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  adminLogout,
  adminResetPassword,
  adminUpdatePassword,
  getAdminDetails,
} from "./adminAuthActions";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createPasswordValidationRegex,
  getCookie,
  getPasswordValidationMessage,
} from "../../utils/Utility";
import { TransitionProps } from "@mui/material/transitions";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { adminGetPasswordConfig } from "../PasswordConfiguration/passwordConfigurationActions";
import { IPasswordConfigData } from "../PasswordConfiguration/passwordConfigurationType";

const theme = createTheme({
  palette: {
    primary: {
      main: "#00BFB3",
    },
    secondary: {
      main: "#2D2D68",
    },
    error: {
      main: "#f44336",
    },
  },
  typography: {
    fontFamily: '"Arial", "Helvetica", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: "none",
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            borderColor: "#e0e0e0",
          },
        },
        input: {
          padding: "10px 14px",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
  },
});

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

function ResetPassword() {
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isToken = searchParams.get("token");
  const email = searchParams.get("email");
  const id = getCookie("id");

  const navigate = useNavigate();
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);
  const { adminDetailsData } = useAppSelector(
    (state: RootState) => state.adminAuth
  );

  const { isSuccess, calledReducerType, message, isError } = useAppSelector(
    (state) => state.common
  );
  const { passwordConfigData } = useAppSelector(
    (state: RootState) => state.adminPasswordConfig
  );

  const [formData, setFormData] = useState({
    email: email || "",
    token: isToken || "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Error state
  const [errors, setErrors] = useState({
    email: "",
    token: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordRegex, setPasswordRegex] = useState<RegExp | null>(null);

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    const date = new Date().toString();
    const logoutRequestBody = {
      logoutTime: date,
    };
    if (
      isSuccess &&
      (calledReducerType === "adminAuth/adminUpdatePassword" ||
        calledReducerType === "adminAuth/adminResetPassword")
    ) {
      dispatch(adminLogout(logoutRequestBody));
      navigate("admin/login");
    }
  };

  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    Transition: React.ComponentType<
      TransitionProps & {
        children: React.ReactElement<any, any>;
      }
    >;
  }>({ open: false, Transition: SlideTransition });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordValidationMessage, setPasswordValidationMessage] =
    useState("");

  useEffect(() => {
    if (passwordConfigData) {
      setPasswordRegex(createPasswordValidationRegex(passwordConfigData));
    }
  }, [passwordConfigData]);

  // Handle input changes
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    setErrors({
      ...errors,
      [name]: "",
    });

    // Validate new password as user types
    if (name === "newPassword" && passwordConfigData) {
      if (value) {
        const passwordError = getPasswordValidationMessage(
          value,
          passwordConfigData
        );
        if (passwordError) {
          setPasswordValidationMessage(passwordError);
        } else {
          setPasswordValidationMessage("");
        }

        // Also validate confirm password when new password changes
        if (formData.confirmPassword) {
          if (value !== formData.confirmPassword) {
            setErrors((prev) => ({
              ...prev,
              confirmPassword: "New password and confirmation do not match",
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              confirmPassword: "",
            }));
          }
        }
      } else {
        setPasswordValidationMessage("");
      }
    }

    // Validate confirm password as user types
    if (name === "confirmPassword") {
      if (value !== formData.newPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "New password and confirmation do not match",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  // Toggle password visibility handlers
  const handleToggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Add this new function to handle confirm password field focus

  // Validate form
  const validateForm = () => {
    let tempErrors = { ...errors };
    let isValid = true;

    if (isToken) {
      // Email validation
      if (!formData.email) {
        tempErrors.email = "Email is required";
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        tempErrors.email = "Please enter a valid email address";
        isValid = false;
      }

      // Token validation
      if (!formData.token) {
        tempErrors.token = "Token is required";
        isValid = false;
      }
    }

    // Current password validation
    if (!formData.oldPassword && !isToken) {
      tempErrors.oldPassword = "Current password is required";
      isValid = false;
    }

    // New password validation
    if (!formData.newPassword) {
      setPasswordValidationMessage("New password is required");
      tempErrors.newPassword = "New password is required";
      isValid = false;
    } else if (passwordConfigData && passwordRegex) {
      const passwordError = getPasswordValidationMessage(
        formData.newPassword,
        passwordConfigData
      );
      if (passwordError) {
        tempErrors.newPassword = passwordError;
        isValid = false;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      tempErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      tempErrors.confirmPassword = "New password and confirmation do not match";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (validateForm()) {
      if (isToken) {
        // Call adminUpdatePassword API when isToken is true
        const updatePasswordPayload = {
          email: formData.email,
          token: formData.token,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        };
        dispatch(adminUpdatePassword(updatePasswordPayload));
      } else {
        // Call adminResetPassword API when isToken is false
        const resetPasswordPayload = {
          email: adminDetailsData.email,
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        };
        dispatch(adminResetPassword(resetPasswordPayload));
      }
    }
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "adminAuth/adminResetPassword" ||
        calledReducerType === "adminAuth/adminUpdatePassword")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);
  useEffect(() => {
    if (id) {
      dispatch(getAdminDetails(id));
    }
  }, []);

  useEffect(() => {
    dispatch(adminGetPasswordConfig());
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="xs"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Logo - Made smaller */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {uiSettings && uiSettings?.logo && (
            <img
              src={`data:image/png;base64,${uiSettings.logo}`}
              alt="Logo Preview"
              style={{ marginBottom: "4px", maxWidth: "140px", height: "auto" }}
            />
          )}
        </Box>

        {/* Form Card - Made more compact */}
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            p: 2.5,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            color="secondary"
            align="center"
            sx={{ fontWeight: "bold", mb: 0.5 }}
          >
            Reset Your Password
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 2 }}
          >
            Please Reset your password to continue
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {isToken && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ mb: 0.5 }}
                >
                  Email
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  variant="outlined"
                  placeholder="Enter your email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  sx={{ mb: errors.email ? 0 : 1.5 }}
                />
                {errors.email && (
                  <FormHelperText error sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                    {errors.email}
                  </FormHelperText>
                )}

                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ mb: 0.5 }}
                >
                  Token
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  variant="outlined"
                  placeholder="Enter token"
                  type="text"
                  name="token"
                  value={formData.token}
                  onChange={handleChange}
                  error={!!errors.token}
                  sx={{ mb: errors.token ? 0 : 1.5 }}
                />
                {errors.token && (
                  <FormHelperText error sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                    {errors.token}
                  </FormHelperText>
                )}
              </>
            )}
            {!isToken && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ mb: 0.5 }}
                >
                  Current Password
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Enter current password"
                  type={showOldPassword ? "text" : "password"}
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  error={!!errors.oldPassword}
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleToggleOldPasswordVisibility}
                          edge="end"
                          size="small"
                        >
                          {showOldPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: errors.oldPassword ? 0 : 1.5 }}
                />
                {errors.oldPassword && (
                  <FormHelperText error sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                    {errors.oldPassword}
                  </FormHelperText>
                )}
              </>
            )}
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mb: 0.5 }}
            >
              New Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Enter new password"
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              error={!!errors.newPassword}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={handleToggleNewPasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showNewPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: errors.newPassword ? 0 : 1.5 }}
            />
            {/* {errors.newPassword && (
              <FormHelperText error sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                {errors.newPassword}
              </FormHelperText>
            )} */}
            {passwordValidationMessage && (
              <FormHelperText
                sx={{
                  mt: 0.5,
                  mb: 1,
                  ml: 1,
                  color: passwordValidationMessage.includes("✓")
                    ? "success.main"
                    : "error.main",
                }}
              >
                {passwordValidationMessage}
              </FormHelperText>
            )}

            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mb: 0.5 }}
            >
              Confirm New Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Confirm new password"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: errors.confirmPassword ? 0 : 2 }}
            />
            {errors.confirmPassword && (
              <FormHelperText error sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                {errors.confirmPassword}
              </FormHelperText>
            )}

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                py: 1,
                mb: 1.5,
                color: "white",
                fontSize: "0.9rem",
                fontWeight: "medium",
              }}
            >
              Reset Password
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                href="/admin/login"
                underline="none"
                color="text.secondary"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "medium",
                  "&:hover": {
                    color: "primary.main",
                  },
                }}
              >
                Back to login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>

      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </ThemeProvider>
  );
}

export default ResetPassword;
